<template>
  <base-input :rules="internalRules" :errors="errors">
    <div
      @focusout="handleBlur"
      @keydown.enter.capture="handleEnter"
      @keydown.tab="handleTab"
      :class="[
        inline
          ? 'relative mt-1'
          : `relative border rounded-md focus-within:ring-1 ring-0 flex items-center transition-all ease-in-out duration-100 bg-white z-auto mt-1 focus-within:ring-${focusColor} focus-within:border-${focusColor} border-${borderColor}`,
      ]"
    >
      <label :class="['absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-normal z-10', `text-${labelColor}`]" v-if="label">{{ label }}</label>
      <datepicker
        :model-value="dateValue"
        :typeable="typeable"
        @update:model-value="handleInput($event)"
        :placeholder="placeholder"
        :input-format="inputFormat"
        :lower-limit="lowerLimit"
        :starting-view="initialView"
        :weekStartsOn="0"
      />
    </div>
  </base-input>
</template>

<script>
import Datepicker from "vue3-datepicker";
import moment from "moment";
import validatable from "@/mixins/validatable";

const ALWAYS_ALLOWED_KEYS = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];

export default {
  mixins: [validatable],
  components: { Datepicker },
  props: {
    modelValue: [Date, String],
    label: String,
    placeholder: String,
    typeable: { type: Boolean, default: true },
    inline: { type: Boolean, default: false },
    size: { type: String, default: "sm" },
    disabledDates: Object,
    minDate: [Date, String],
    initialView: { type: String, default: "day" },
  },
  emits: ["update:modelValue"],

  mounted() {
    this._inputEl = this.$el.querySelector("input");
    if (this._inputEl) {
      this._inputEl.addEventListener("keydown", this.restrictInput);
    }
  },

  beforeUnmount() {
    if (this._inputEl) {
      this._inputEl.removeEventListener("keydown", this.restrictInput);
    }
  },

  computed: {
    focusColor() {
      return this.hasError ? "red-500" : "primary-500";
    },
    borderColor() {
      return this.hasError ? "red-500" : "gray-200";
    },
    labelColor() {
      return this.hasError ? "red-500" : "gray-900";
    },
    placeholderColor() {
      return this.hasError ? "red-400" : "gray-500";
    },
    inputFormat() {
      return "MMMM d, yyyy";
    },
    dateValue() {
      return this.convertToDate(this.modelValue);
    },
    lowerLimit() {
      if (this.disabledDates && this.disabledDates.to) return this.convertToDate(this.disabledDates.to);
      if (this.minDate) return this.convertToDate(this.minDate);
      return undefined;
    },
    dateFormats() {
      return [
        "MMMM D, YYYY",
        "MMM D, YYYY",
        "MMMM D YYYY",
        "MMM D YYYY",
        "M/D/YYYY",
        "MM/DD/YYYY",
        "M-D-YYYY",
        "MM-DD-YYYY",
        "YYYY-MM-DD",
        "M/D/YY",
        "MM/DD/YY",
        "M-D-YY",
        "MM-DD-YY",
      ];
    },
  },

  methods: {
    restrictInput(event) {
      if (ALWAYS_ALLOWED_KEYS.includes(event.key)) return;
      if (event.ctrlKey || event.metaKey) return;

      // Only allow: letters, digits, space, / - , .
      const allowed = /^[a-zA-Z0-9 /\-,.]$/;
      if (!allowed.test(event.key)) {
        event.preventDefault();
      }
    },

    emitDate(value) {
      const date = value instanceof Date ? value : this.convertToDate(value);
      this.$emit("update:modelValue", date ? moment(date).format("YYYY-MM-DD") : null);
    },

    handleInput(event) {
      this.validate();
      this.emitDate(event);
    },

    parseTypedDate(raw) {
      const trimmed = (raw || "").trim();
      if (!trimmed) return null;

      const parsed = moment(trimmed, this.dateFormats, true);
      if (!parsed.isValid()) return null;

      const date = parsed.toDate();
      const current = this.convertToDate(this.modelValue);
      if (current && date.getTime() === current.getTime()) return null;

      return date;
    },

    commitTypedDate() {
      const inputEl = this.$el.querySelector("input");
      if (!inputEl) return false;

      const date = this.parseTypedDate(inputEl.value);
      if (!date) return false;

      this.validate();
      this.emitDate(date);
      return true;
    },

    handleEnter(event) {
      if (!this.commitTypedDate()) return;
      event.preventDefault();
      event.stopPropagation();
      const inputEl = this.$el.querySelector("input");
      if (inputEl) inputEl.blur();
    },

    handleTab() {
      // Commit the typed value before focus leaves the field.
      this.commitTypedDate();

      const popout = this.$el.querySelector(".v3dp__popout");
      if (popout) popout.style.display = "none";
    },

    handleBlur(event) {
      if (event.relatedTarget && this.$el.contains(event.relatedTarget)) return;
      const inputEl = event.target;
      if (!inputEl || inputEl.tagName !== "INPUT") return;
      this.commitTypedDate();
    },

    reset() {
      this.isResetting = true;
      this.$emit("update:modelValue", null);
    },

    convertToDate(dateValue) {
      if (!dateValue) return null;
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === "string") {
        return moment(dateValue, "YYYY-MM-DD").toDate();
      }
      return dateValue;
    },
  },
};
</script>

<style>
.v3dp__input_wrapper input,
.v3dp__popout .v3dp__elements button {
  font-size: 0.875rem;
}

.v3dp__element__button__day[disabled] {
  visibility: hidden;
}
</style>
